import {
  ChangeDetectorRef, Component, HostBinding, EventEmitter, Input,
  OnInit, OnChanges, OnDestroy, Output, SimpleChanges,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable } from 'rxjs';

import { ActiveFilterModel } from '../active-filters/active-filter.model';
import { FilterSearchModel } from './filter-search.model';
import { VEHICLE_FILTER_TYPE, VCDB_FILTER_TYPE } from '../active-filters/active-filter-types';
import {
  REGION_TYPE,
  VEHICLE_TYPE,
  MAKE_TYPE,
  MODEL_TYPE,
  SUB_MODEL_TYPE,
  YEAR_START_TYPE,
  YEAR_END_TYPE,
} from '../advanced-search/filter-types';
import { filterPlaceholders } from '../advanced-search/filter-placeholders';
import { createOption } from '../../form/select/option-helpers';
import { BaseAdvancedSearchComponent } from '../advanced-search/_base-advanced-search.component';
import { SelectDropdownComponent } from '../../dropdown/select-dropdown/select-dropdown.component';

import { BrandService } from '../../brand/brand.service';
import { FitmentsService } from '../../fitments/fitments.service';


@Component({
  moduleId: module.id,
  selector: 'vehicle-search',
  templateUrl: 'vehicle-search.component.html',
  styleUrls: ['vehicle-search.component.scss'],
})
export class VehicleSearchComponent implements OnInit, OnDestroy {

  @Input() attributesList: Array<any> = []; // all attributes list
  @Input() ghostValue: string;
  @Input() filters: Array<ActiveFilterModel> = []; // all active filters
  @Input() filterPlaceholders: any = filterPlaceholders;
  @Input() collapseMultipleSelect: boolean = false;
  @Input() collapsed: boolean = false;
  @Input() productId: any;
  // to avoid dropdowns menu clipping because of overflow, we can show menu on top
  @Input() dropdownsUp: boolean = false;
  @Input() showVehicleAttrs: Array<string>; // if we need to show only certain attrs
  @Input() isMakeMultiple: boolean = false;
  @Input() showTitles: boolean = false;
  @Input() allowSelectAllOptions: boolean = false;

  @Output() fetchOptions: EventEmitter<any> = new EventEmitter();
  @Output() selectChange: EventEmitter<any> = new EventEmitter();
  @Output() allOptionsSelected: EventEmitter<any> = new EventEmitter();

  filtersModel: any = {
    [MAKE_TYPE]: [],
    [MODEL_TYPE]: [],
    [SUB_MODEL_TYPE]: [],
    [YEAR_START_TYPE]: [],
    [YEAR_END_TYPE]: [],
  };
  filterNamesOrder: Array<string> = [
    MAKE_TYPE,
    MODEL_TYPE,
    SUB_MODEL_TYPE,
    YEAR_START_TYPE,
    YEAR_END_TYPE,
  ];
  selected: any = {
    [MAKE_TYPE]: {},
    [MODEL_TYPE]: {},
    [SUB_MODEL_TYPE]: {},
    [YEAR_START_TYPE]: {},
    [YEAR_END_TYPE]: {},
  };
  disabled: any = {
    [REGION_TYPE]: false,
    [VEHICLE_TYPE]: false,
    [MAKE_TYPE]: false,
    [MODEL_TYPE]: false,
    [SUB_MODEL_TYPE]: false,
    [YEAR_START_TYPE]: false,
    [YEAR_END_TYPE]: false,
  };
  multipleSelectData: any = {
    [REGION_TYPE]: [],
    [VEHICLE_TYPE]: [],
  };

  filterSearchModel: any = new FilterSearchModel();
  isActive: boolean = true;
  optionName: string = 'title';
  optionId: string = 'option';

  constructor(
    public brandService: BrandService,
    private fitmentsService: FitmentsService,
    private ref: ChangeDetectorRef,
  ) {}

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get vehicleFilters(): Array<any> {
    return _.filter(this.filters, (filter: any) => {
      return filter.type === VEHICLE_FILTER_TYPE;
    });
  }

  get selectedValues(): Array<any> {
    return _.values(this.selected);
  }

  get filterFakeId(): string {
    return `${VEHICLE_FILTER_TYPE}_${this.vehicleFilters.length}`;
  }

  get combinedFilters(): Array<ActiveFilterModel> {
    const filter = this.getFilterFromDropdowns();
    const filters = this.getFiltersFromMultipleSelects();
    if (filter) {
      _.set(filter, 'title', this.getCombinedFilterTitle(filter.data.options));
    }
    return _.compact([...[filter], ...filters]);
  }

  get isShowVehicleAttrs() {
    return !_.isNil(this.showVehicleAttrs);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    if (this.isMakeMultiple) {
      this.filterSearchModel[MAKE_TYPE] = [];
      this.fetchOptions.emit(MAKE_TYPE);
    }
  }

  ngOnDestroy() {
    this.isActive = false;
    this.filterSearchModel = new FilterSearchModel();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  // dropdown select change
  onSelectChange(selected: any, name: string) {
    this.selected[name].type = name;
    this.filterSearchModel[name] = selected.option;
    switch (name) {
      case MAKE_TYPE:
        this.handleMakeChange();
        break;
      default:
        break;
    }
    this.selectChange.emit({selected, name});
  }

  // select-options option changed
  onOptionSelected(selected: any, name: string) {
    this.selected[name].type = name;
    if (selected.selected) {
      this.filterSearchModel[name].push(selected.option);
    } else {
      _.pull(this.filterSearchModel[name], selected.option);
    }
    this.selectChange.emit({selected, name});
  }

  // select-options Select all changed
  onAllOptionsSelected(selectedOptions: Array<any>, name: string) {
    this.selected[name].type = name;
    this.filterSearchModel[name] = [..._.map(selectedOptions, 'option')];
    this.allOptionsSelected.emit({selectedOptions: selectedOptions, name});
  }

  onDropdownOpen(isOpened: boolean, name: string) {
    if (!isOpened) {
      this.filterSearchModel[name] = this.selected[name].option;
      return;
    }
    if (name === MODEL_TYPE && !this.selected[MAKE_TYPE].option) { return; }
    this.filterSearchModel[name] = null;
    this.filtersModel[name] = [this.getPlaceholder(name)];
    this.fetchOptions.emit(name);
  }

  onMultipleOptionClick(option: any, name: string) {
    option.selected = !option.selected;
    if (option.selected) {
      this.filterSearchModel[name].push(option.option);
    } else {
      _.pull(this.filterSearchModel[name], option.option);
    }
  }

  onDropdownClear(name: string) {
    const index = _.indexOf(this.filterNamesOrder, name);
    const names = _.slice(this.filterNamesOrder, index);
    this.setPlaceHolders(names);
    _.each(names, (selectName: string) => {
      this.filtersModel[selectName] = [];
    });
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public setPlaceHolder(filterType: string): void {
    this.filterSearchModel[filterType] = null;
    this.selected[filterType] = this.getPlaceholder(filterType);
  }

  public setPlaceHolders(filterTypes: Array<string>): void {
    _.each(filterTypes, (type: string) => {
      this.setPlaceHolder(type);
    });
  }

  public disableSelecbox(disable: boolean, filterName: string): void {
    this.disabled[filterName] = disable;
  }

  /*
    * Set default placeholder to all dropdown selecboxes
    * Reset multiple select boxes
    * Reset search model which depends on selecboxes
  */
  public resetSelectboxes(): void {
    this.filterNamesOrder.forEach((value: string) => {
      this.setPlaceHolder(value);
      if (value === MAKE_TYPE && this.isMakeMultiple) {
        this.filtersModel[value] = _.map(this.filtersModel[value], item => {
          item.selected = false;
          return item;
        });
        this.ref.markForCheck();
      } else {
        this.filtersModel[value] = [this.getPlaceholder(value)];
      }
      this.disableSelecbox(false, value);
    });
    this.filterSearchModel = new FilterSearchModel();
    _.each(this.multipleSelectData, (value: any, key: string) => {
      this.multipleSelectData[key] = [];
    });
    if (this.isMakeMultiple) {
      this.filterSearchModel[MAKE_TYPE] = [];
    }
  }

  /*
    * Allows to set year/make/model outside this component
    * For example after select vehicle via predictive search
  */
  public setYearMakeModel(data?: any) {
    // set selected make model year
    if (data) {
      if (data.make) { this.selected.make = _.assign(data.make, {type: MAKE_TYPE}); }
      if (data.model) { this.selected.model = _.assign(data.model, {type: MODEL_TYPE}); }
      if (data.year) {
        this.selected[YEAR_START_TYPE] = _.assign({}, data.year, {type: YEAR_START_TYPE});
      }
    }
  }

  /*
    * Find nested filter by filter name (region, vehicleType, minYear, etc)
  */
  public findActiveFilter(filterName: string): ActiveFilterModel {
    const filter: any = this.findCombinedActiveFilter(filterName);
    return _.find(_.get(filter, 'data.options', []), ['type', filterName]);
  }

  /*
    * Find combined filter by nested option name (region, vehicleType, minYear, etc)
  */
  public findCombinedActiveFilter(name: string): ActiveFilterModel {
    return _.find(this.vehicleFilters, (activefilter: ActiveFilterModel) => {
      return _.some(activefilter.data.options, (filter: ActiveFilterModel) => {
        return filter.type === name;
      });
    });
  }

  /*
    * Pre-set make/model/submodel/year according on active filters
    * Update SearchFiltersModel according on active filters
  */
  public setActiveFiltersData(): void {
    this.filterNamesOrder.forEach((filterName: string) => {
      const activeFilter: any = this.findActiveFilter(filterName);
      if (activeFilter) {
        const { option, title } = activeFilter;
        this.disableSelecbox(true, filterName);
        this.filterSearchModel[filterName] = option;
        this.selected[filterName] = { option, title, type: filterName };
        return;
      }
      this.filterSearchModel[filterName] = null;
    });
  }

  /*
    * Set data to dropdowns or multiple selecboxes by name
    * Called after fetching data from server
  */
  public setSelectData(data: Array<any>, name: string): void {
    if (name === REGION_TYPE || name === VEHICLE_TYPE) {
      // multiple selectboxes
      // set already selected options, but which was not added as a active filter yet
      const selected = _.filter(this.multipleSelectData[name], ['selected', true]);
      const itemsForSelect = _.intersectionBy(data, selected, 'option');
      _.each(itemsForSelect, (item: any) => item.selected = true);
      this.multipleSelectData[name] = data || [];
      // if there is region or vehicle type active filters, then
      // pre select active multiple select boxes
      this.setActiveMultipleSelect(name);
      // remove selected options from search model if they was removed
      _.remove(this.filterSearchModel[name], (option: number) => {
        return !_.some(data, (item: any) => item.option === option);
      });
    } else {
      if (name !== MAKE_TYPE || !this.isMakeMultiple) {
        data.unshift(this.getPlaceholder(name));
      }
      this.filtersModel[name] = data;
    }
  }

  /*
    * Update selectboxes related to the filter which was removed
    * (Enable dropdowns or remove selected multiple selecboxes)
  */
  public handleFilterRemove(filter: ActiveFilterModel): void {
    _.each(filter.data.options, (item: any) => {
      if (this.isMultipleSelect(item.type)) {
        const option = _.find(this.multipleSelectData[item.type], ['option', item.option]);
        _.set(option, 'selected', false);
        _.pull(this.filterSearchModel[item.type], item.option);
      } else {
        this.disableSelecbox(false, item.type);
        this.setPlaceHolder(item.type);
      }
    });
    this.fetchOptions.emit(REGION_TYPE);
    this.fetchOptions.emit(VEHICLE_TYPE);
  }

  public showAttr(attrType: string) {
    if (!this.isShowVehicleAttrs) { return true; }
    return _.indexOf(this.showVehicleAttrs, attrType) >= 0;
  }

  public getFiltersData(): void {
    return this.filterSearchModel;
  }


  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private getPlaceholder(filterType: string): any {
    const placeholder = this.filterPlaceholders[filterType];
    if (filterType === MODEL_TYPE) {
      placeholder[this.optionName] = placeholder[this.selected.make[this.optionId] === 0 ? 'empty_make_name' : 'selected_make_name'];
    }
    return placeholder;
  }

  private getOptionTitle(option: any): string {
    return _.get(option, this.optionId, 0) !== 0 ? option[this.optionName] : '';
  }

  private findOptionByType(options: Array<any>, optionType: string): any {
    return _.find(options, ['type', optionType]);
  }

  /*
    * Get title from all selected dropdowns
  */
  private getCombinedFilterTitle(options: Array<any>): string {
    const yearEndTitle = this.getOptionTitle(this.findOptionByType(options, YEAR_END_TYPE));
    const yearStartTitle = this.getOptionTitle(this.findOptionByType(options, YEAR_START_TYPE));
    return _.reduce(this.filterNamesOrder, (result: string, filterName: string) => {
      // don't set filter title if filter was already selected
      if (this.findActiveFilter(filterName)) {
        return result;
      }
      if ((filterName === YEAR_END_TYPE) && yearEndTitle) {
        if (yearEndTitle === yearStartTitle) { return result; }
        if (!yearStartTitle) { return result + yearEndTitle; }
        return result + (yearEndTitle ? '-' + yearEndTitle : '');
      }
      return result + ' ' + this.getOptionTitle(this.findOptionByType(options, filterName));
    }, '').trim();
  }

  /*
    * Output structure example
    * [
        {
          _id: this.filterFakeId
          title: '',
          type: VEHICLE_FILTER_TYPE,
          data: {
            options: [
              {
                option: 1,
                title: 'Honda',
                type: MAKE_TYPE,
                data: { attributeInfo: { attribute_name, column_name, etc..} }
              },
              {the same with model, subModel, years}
            ]
          }
        }
     ]
  */
  private getFilterFromDropdowns(): ActiveFilterModel {
    return _.reduce(this.selectedValues, (result: any, selectedDropdown: any) => {
      if (selectedDropdown[this.optionId] === 0) { return result; }
      if (this.disabled[selectedDropdown.type]) { return result; }
      const attributeInfo = _.find(this.attributesList, ['attribute_name', selectedDropdown.type]);
      _.set(selectedDropdown, 'data.attributeInfo', attributeInfo);
      if (!result) {
        return new ActiveFilterModel(
          this.filterFakeId,
          '', // add filter title after all filters will be set
          VEHICLE_FILTER_TYPE,
          {options: [selectedDropdown]}
        );
      } else {
        result.data.options.push(selectedDropdown);
        return result;
      }
    }, null);
  }

  /*
    * Output structure example
    [
        {
          _id: this.filterFakeId
          title: 'Mexico, United States',
          type: VEHICLE_FILTER_TYPE,
          data: {
            multiple: true,
            options: [
              {
                option: 1,
                title: 'Mexico',
                type: REGION_TYPE,
                attributeInfo: {attribute_name, column_name, etc..}
              },
              {option: 2, title: 'United States', ...etc}
            ]
          }
        }
     ]
  */
  private getFiltersFromMultipleSelects(): Array<ActiveFilterModel> {
    return _.reduce(this.multipleSelectData, (result: any, options: Array<any>, type: string) => {
      const selected = _.filter(options, ['selected', true]);
      // add attrubute data for future column creating
      const attributeData = _.find(this.attributesList, ['attribute_name', type]);
      const filter = _.reduce(selected, (_filter: any, option: any) => {
        option.type = type;
        _.set(option, 'data.attributeInfo', attributeData);
        if (!_filter) {
          return new ActiveFilterModel(
            `${this.filterFakeId}_${type}`,
            option.title,
            VEHICLE_FILTER_TYPE,
            {options: [option], multiple: true},
          );
        } else {
            _filter.title += `, ${option.title}`;
            _filter.data.options.push(option);
          return _filter;
        }
      }, null);
      result.push(filter);
      return result;
    }, []);
  }

  /*
    * Pre-select multiple options in the list, those that exist in the active filter
  */
  private setActiveMultipleSelect(name: string): void {
    const activeFilter: any = this.findCombinedActiveFilter(name);
    if (!activeFilter) { return; }
    _.each(activeFilter.data.options, (item: any) => {
      const option = _.find(this.multipleSelectData[name], ['option', item.option]);
      _.set(option, 'selected', true);
      this.filterSearchModel[name] = _.chain(this.filterSearchModel[name])
        .concat([item.option]).uniq().value();
    });
  }

  private isMultipleSelect(type: string): boolean {
    return _.includes([REGION_TYPE, VEHICLE_TYPE], type);
  }

  private handleMakeChange(): void {
    this.setPlaceHolder(MODEL_TYPE);
    if (this.selected.make.option === 0) {
      if (this.isMakeMultiple) {
        this.filtersModel.model = [];
      } else {
        this.filtersModel.model = [this.getPlaceholder(MODEL_TYPE)];
      }
    }
  }
}
